/**
 * admin_business_controller.js — Admin actions on one business (Stimulus).
 *
 * Activate/deactivate, mark as test, admin plan override (permanent, or a trial
 * for a number of days when the second prompt is answered), the override reason,
 * and delete (and the detail page's placeholder Backup button). Each action confirms, calls the business's JSON route under
 * /admin/api/businesses/<id>, toasts the answer and re-renders the page in
 * place through window.chippiePageCheck.refresh(): the list's statistics cards,
 * its status filter and the detail page's status all derive from the change,
 * so the server's render of the whole page is the one owner of what shows.
 *
 * Every action takes the business as params: data-admin-business-id-param and
 * data-admin-business-name-param on the triggering element, plus the action's
 * own param (active, test, plan, reason). Values: deletedUrl, where to go after
 * a delete when the page shown is the deleted business's own (blank: refresh).
 *
 * Templates: templates/admin/business_management.html, templates/admin/business_detail.html
 */

import { Controller } from "@hotwired/stimulus";

const PLAN_LABELS = { professional: 'Pro', team: 'Team' };

export default class extends Controller {
    static values = { deletedUrl: String };

    toggleStatus(event) {
        const { id, name, active } = event.params;
        const verb = active ? 'Deactivate' : 'Activate';
        if (!confirm(`${verb} business "${name}"?`)) return;
        this._send(id, '/toggle-status', 'POST', null, `${verb}d "${name}"`);
    }

    toggleTest(event) {
        const { id, name, test } = event.params;
        const verb = test ? 'unmark' : 'mark';
        if (!confirm(`Are you sure you want to ${verb} "${name}" as a test business?`)) return;
        this._send(id, '/toggle-test', 'POST', null, test ? `"${name}" is no longer a test business` : `"${name}" marked as a test business`);
    }

    upgradePlan(event) {
        const { id, name, plan } = event.params;
        const label = PLAN_LABELS[plan] || plan;
        const reason = prompt(`Upgrade "${name}" to the ${label} plan?\n\nThis is an admin override — no Stripe billing will be created.\n\nReason for override:`);
        if (reason === null) return;
        const days = prompt('Trial length in days (leave blank for a permanent override):');
        if (days === null) return;
        const body = { plan_id: plan, reason: reason };
        if (days.trim()) body.trial_days = parseInt(days, 10);
        this._send(id, '/upgrade-plan', 'POST', body, `"${name}" is now on ${label}`);
    }

    editOverrideReason(event) {
        const { id, name, reason: current } = event.params;
        const reason = prompt(`Edit override reason for "${name}":`, current);
        if (reason === null) return;
        this._send(id, '/override-reason', 'PUT', { reason: reason }, 'Override reason saved');
    }

    delete(event) {
        const { id, name } = event.params;
        if (!confirm(`Permanently delete business "${name}"? This action cannot be undone and will delete all associated data.`)) return;
        this._send(id, '', 'DELETE', null, `Deleted "${name}"`).then((done) => {
            if (done && this.deletedUrlValue) window.Turbo.visit(this.deletedUrlValue);
        });
    }

    /** The detail page's Backup button: there is no backup route yet. */
    backup() {
        if (!confirm('Create a backup of this business data?')) return;
        alert('Business data backup functionality would be implemented here.');
    }

    /** Resolves true when the server accepted the change. */
    _send(id, path, method, body, success) {
        const options = { method: method, credentials: 'same-origin' };
        if (body) {
            options.headers = { 'Content-Type': 'application/json' };
            options.body = JSON.stringify(body);
        }
        return fetch(`/admin/api/businesses/${id}${path}`, options)
            .then(function (response) { return response.json(); })
            .then((data) => {
                if (!data.success) throw new Error(data.message || 'Unknown error');
                window.showNotification(success, 'success');
                if (!(method === 'DELETE' && this.deletedUrlValue)) window.chippiePageCheck.refresh();
                return true;
            })
            .catch(function (error) {
                window.showNotification('Failed: ' + error.message, 'error');
                return false;
            });
    }
}
